import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { Store } from '../../../services/store';
import { SessionStore } from '../../../services/session.store';
import { toVoid } from '../../../utils/void';
import { PasswordLinkDto } from './models/password-link.dto';
import { SetPasswordProvider } from './set-password.provider';

// Mirrors SetPassword.MinPasswordLength on the API (0057, decision 2): length is the one rule
export const MIN_PASSWORD_LENGTH = 8;

// 'checking' until the check call answers; 'dead' covers unknown, used and expired alike (the API answers 404 for all)
export type PasswordLinkStatus = 'checking' | 'live' | 'dead';

export type SetPasswordError = 'tooShort' | 'mismatch' | 'failed';

export interface SetPasswordState {
  token: string;
  linkStatus: PasswordLinkStatus;
  link: PasswordLinkDto | undefined;
  submitting: boolean;
  error: SetPasswordError | undefined;
}

const INITIAL_STATE: SetPasswordState = {
  token: '',
  linkStatus: 'checking',
  link: undefined,
  submitting: false,
  error: undefined
};

@Injectable({ providedIn: 'root' })
export class SetPasswordStore extends Store<SetPasswordState> {
  constructor(
    private provider: SetPasswordProvider,
    private sessionStore: SessionStore
  ) {
    super(INITIAL_STATE);
  }

  // The token comes from the URL fragment; without one there is nothing to check
  init(token: string): Observable<void> {
    this.update(state => ({ ...state, token }));

    if (!token) {
      this.update(state => ({ ...state, linkStatus: 'dead' }));
      return of(undefined);
    }

    return this.provider.checkPasswordLink(token).pipe(
      tap(link => this.update(state => ({ ...state, linkStatus: 'live', link }))),
      catchError(() => {
        this.update(state => ({ ...state, linkStatus: 'dead', link: undefined }));
        return of(undefined);
      }),
      toVoid()
    );
  }

  // Too short and mismatch are caught here, before the call; on success the session store takes the new session
  setPassword(password: string, repeated: string): Observable<void> {
    if (password.length < MIN_PASSWORD_LENGTH) {
      this.update(state => ({ ...state, error: 'tooShort' }));
      return of(undefined);
    }

    if (password !== repeated) {
      this.update(state => ({ ...state, error: 'mismatch' }));
      return of(undefined);
    }

    this.update(state => ({ ...state, submitting: true, error: undefined }));

    return this.provider.setPassword(this.state.token, password).pipe(
      tap(session => {
        this.sessionStore.accept(session);
        this.update(state => ({ ...state, submitting: false }));
      }),
      catchError((error: unknown) => {
        // 404 means the link died between the check and the submit — the page switches to its dead-link message
        if (error instanceof HttpErrorResponse && error.status === 404) {
          this.update(state => ({ ...state, submitting: false, linkStatus: 'dead', link: undefined }));
        } else {
          this.update(state => ({ ...state, submitting: false, error: 'failed' }));
        }

        return of(undefined);
      }),
      toVoid()
    );
  }
}
